import type { ReactElement, ReactNode } from 'react';
import { Children, cloneElement } from 'react';

import { omit } from 'pkg/objects';
import type { RouteProps } from 'pkg/router/route';

export interface ParsedRoute extends RouteProps {
	parentPattern?: string;
	guid: string;
	path: string;
	componentName: string;
	isRoot: boolean;
	pattern?: string;
	/**
	 * Route params that have been parsed, for example "current tab" and their values
	 */
	urlParams?: Record<string, string>;
	query?: Record<string, string>;
}

let parsedRoutes: ParsedRoute[] = [];

export const getParsedRoutes = (): ParsedRoute[] => parsedRoutes;

/**
 *	Parses as route tree into a flat set of route definitions.
 */
export default function parser(children: ReactNode): ParsedRoute[] {
	const parse = (
		children: ReactNode,
		parent: ReactElement<RouteProps> | null = null
	): (ParsedRoute | null)[] => {
		const childrenArray = Array.from(
			Children.toArray(children)
		) as ReactElement<RouteProps>[];

		return []
			.concat(
				...childrenArray.map((child) => {
					let { path, redirectTo, component, before, after } = child.props;
					let parentPattern: string | undefined, guid: string;

					if (!path) {
						path = '/';
					}

					// Prepend parent path if parent is present
					if (parent) path = [parent.props.path, path].join('/');

					// Clean the path from leading and trailing slashes
					path = path.replace(/\/+/g, '/').replace(/\/+$/, '');

					if (redirectTo) {
						redirectTo = redirectTo.replace(/\/+/g, '/').replace(/\/+$/, '');
					}

					if (!before) {
						before = [];
					}

					if (!after) {
						after = [];
					}

					// Inherit parent component and middlewares
					if (parent && parent.props) {
						if (parent.props.component && !component) {
							component = parent.props.component;
						}

						if (parent.props.before && parent.props.before.length > 0) {
							before = [...parent.props.before, ...before];
						}

						if (parent.props.after && parent.props.after.length > 0) {
							after = [...parent.props.after, ...after];
						}
					}

					before = before.filter(
						(callback: any) => callback instanceof Function
					);
					after = after.filter((callback: any) => callback instanceof Function);

					const patternProps: any = {};

					if (parent && parent.props) {
						Object.entries(omit(parent.props, 'children')).forEach(
							([key, value]) => {
								if (!child.props.hasOwnProperty(key)) {
									patternProps[key] = value;
								}
							}
						);

						if (path !== parent.props.path) {
							parentPattern = parent.props.path;
						}
					}

					guid = `@${path || 'root'}`.replace(':', '').replace(/\/+/g, '.');

					let isRoot =
						!parent &&
						child.props.children &&
						Children.count(child.props.children) > 0;

					// @NOTE Make sure IndexRoute is root
					if (!isRoot && child.props.path === '/') {
						isRoot = true;
					}

					let componentName = 'unnamed';

					if ((component as any)?.name) {
						componentName = (component as any).name;
					} else if ((component as any)?.displayName) {
						componentName = (component as any).displayName;
					}

					let route: ParsedRoute | null = {
						...child.props,
						...patternProps,
						parentPattern,
						guid,
						path,
						redirectTo,
						component,
						componentName,
						before,
						after,
						isRoot,
					};

					// Ignore empty components but keep props, except if its a redirect route
					if (!component && !redirectTo) route = null;

					if (child.props.children) {
						// @NOTE Pass in inherited props from parent
						let nextChild = cloneElement(child, {
							path,
							component,
							before,
							after,
						});

						return [route, ...parse(child.props.children, nextChild)];
					}

					return route;
				})
			)
			.filter((n): n is ParsedRoute => n !== null);
	};

	const uniqueGuids: string[] = [];

	const routes = parse(children)
		.map((route) => omit(route, 'children'))
		.filter((route): route is ParsedRoute => {
			if (!uniqueGuids.includes(route.guid)) {
				uniqueGuids.push(route.guid);

				// Create a named key based on path, if not already set
				if (!route.name) {
					let namedKeyParts = route.path
						.split('/')
						.map((n: string) => n.replace(/:\w+/, ''))
						.filter((n: string) => n);

					namedKeyParts = [...new Set(namedKeyParts)];

					route.name = namedKeyParts.join('.');
				}

				return true;
			}

			return false;
		});

	const names = routes.map((r) => r.name);

	const dupedNames = ((list: string[]) => {
		let seen = new Set<string>();
		let store: string[] = [];

		list.filter(
			(item) =>
				seen.size === seen.add(item).size &&
				!store.includes(item) &&
				store.push(item)
		);

		return store;
	})(names);

	if (dupedNames.length > 0) {
		throw new Error(
			`Route names should be unique, clashing names are: ${dupedNames.join(
				', '
			)}`
		);
	}

	parsedRoutes = routes;

	return routes;
}
